import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

// Scroll to top on route change
export function ScrollToTop() {
  // `key` changes on every navigation, including a click through to the page
  // you are already on — without it, an anchor link only works once.
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (hash) {
      // Sections render as their data arrives and images change the page height,
      // so keep correcting the position until it stops moving.
      const id = decodeURIComponent(hash.slice(1));
      let tries = 0;
      let lastTop = null;
      let settled = 0;
      const tick = () => {
        const el = document.getElementById(id);
        if (el) {
          const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
          settled = lastTop !== null && Math.abs(top - lastTop) < 2 ? settled + 1 : 0;
          lastTop = top;
          el.scrollIntoView({ behavior: tries === 0 ? "smooth" : "auto", block: "start" });
          if (settled >= 3) return;
        }
        if (tries++ < 30) setTimeout(tick, 100);
      };
      tick();
      return;
    }
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash, key]);
  return null;
}

// Reveal on scroll wrapper
export function Reveal({ children, className = "", delay = 0, as: Tag = "div" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "in-view" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

// Section heading with orange accent
export function SectionTitle({ eyebrow, title, subtitle, light = false, center = false }) {
  return (
    <div className={`${center ? "text-center mx-auto max-w-2xl" : "max-w-2xl"} mb-12`}>
      {eyebrow && (
        <span className="inline-block text-orange font-bold text-sm tracking-widest uppercase mb-3">
          {eyebrow}
        </span>
      )}
      <h2 className={`font-display font-extrabold text-3xl md:text-4xl leading-tight ${light ? "text-white" : "text-navy"}`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-4 text-base leading-relaxed ${light ? "text-slate-300" : "text-slate-500"}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}
